// A small text tip for icon-only controls and truncated values (the collapsed nav rail, toolbar buttons). Shows after
// a short hover delay or at once on keyboard focus, hides on leave, blur, Escape or press. Rendered as a top-layer
// popover next to its trigger, and linked with aria-describedby unless it only repeats the trigger's own name.

import {
  cloneElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { cn } from './cn';
import { hidePopoverSafe, showPopoverSafe, useAnchoredPosition, type Placement } from './position';

interface TooltipChildProps {
  ref?: Ref<HTMLElement>;
  onPointerEnter?: (event: PointerEvent<HTMLElement>) => void;
  onPointerLeave?: (event: PointerEvent<HTMLElement>) => void;
  onPointerDown?: (event: PointerEvent<HTMLElement>) => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  'aria-describedby'?: string;
}

export interface TooltipProps {
  content: ReactNode;
  /** One element that forwards ref and the pointer/focus handlers (ui buttons, router Links, plain elements). */
  children: ReactElement<TooltipChildProps>;
  placement?: Placement;
  /** Hover delay in ms. */
  delay?: number;
  disabled?: boolean;
  /**
   * Link the tip with aria-describedby (default). Set false when the tip repeats the trigger's accessible name
   * (icon-only nav links that already carry an aria-label), so it is not read twice.
   */
  describe?: boolean;
  className?: string;
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
}

export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 450,
  disabled = false,
  describe = true,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const tipId = `tip${useId()}`;

  const clear = () => window.clearTimeout(timer.current);
  const show = (immediate: boolean) => {
    clear();
    if (disabled) return;
    if (immediate) setOpen(true);
    else timer.current = window.setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    clear();
    setOpen(false);
  };

  useEffect(() => clear, []);
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const props = children.props;
  const child = cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
      setRef(props.ref, node);
    },
    onPointerEnter: (e: PointerEvent<HTMLElement>) => {
      props.onPointerEnter?.(e);
      if (e.pointerType !== 'touch') show(false);
    },
    onPointerLeave: (e: PointerEvent<HTMLElement>) => {
      props.onPointerLeave?.(e);
      hide();
    },
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      props.onPointerDown?.(e);
      hide();
    },
    onFocus: (e: FocusEvent<HTMLElement>) => {
      props.onFocus?.(e);
      // Keyboard focus only; a click also focuses and should not pop a tip.
      try {
        if (e.currentTarget.matches(':focus-visible')) show(true);
      } catch {
        show(true);
      }
    },
    onBlur: (e: FocusEvent<HTMLElement>) => {
      props.onBlur?.(e);
      hide();
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      props.onKeyDown?.(e);
      if (e.key === 'Escape' && open) {
        e.stopPropagation();
        hide();
      }
    },
    'aria-describedby':
      describe && open && !disabled
        ? [props['aria-describedby'], tipId].filter(Boolean).join(' ')
        : props['aria-describedby'],
  });

  return (
    <>
      {child}
      {open && !disabled && (
        <TooltipBubble id={tipId} anchorRef={anchorRef} placement={placement} hidden={!describe} className={className}>
          {content}
        </TooltipBubble>
      )}
    </>
  );
}

function TooltipBubble({
  id,
  anchorRef,
  placement,
  hidden,
  className,
  children,
}: {
  id: string;
  anchorRef: { readonly current: HTMLElement | null };
  placement: Placement;
  hidden: boolean;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    showPopoverSafe(el);
    return () => hidePopoverSafe(el);
  }, []);
  useAnchoredPosition(true, anchorRef, ref, placement, 8);
  return (
    <div
      ref={ref}
      id={id}
      role="tooltip"
      aria-hidden={hidden || undefined}
      popover="manual"
      style={{ top: 0, left: 0 }}
      className={cn(
        'pointer-events-none fixed inset-auto m-0 max-w-[280px] rounded-chip border-0 px-2.5 py-1.5',
        'bg-navy-deep text-xs font-medium text-white shadow-raised animate-fade-in dark:bg-[#2a3346]',
        'z-[var(--gt-z-popover)]',
        className,
      )}
    >
      {children}
    </div>
  );
}
